import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Download,
  CreditCard,
  Calendar,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Wrench,
  Bot,
  ChevronLeft,
  ChevronRight,
  Plus,
  Settings,
  X,
} from 'lucide-react';
import adminCompanyService from '../services/adminCompanyService';
import adminSubscriptionService from '../services/adminSubscriptionService';
import { adminPlansService } from '../services/adminPlansService';
import BlockSlotConfig from '../components/billing/BlockSlotConfig';

const PAGE_SIZE = 20;

const SubscriptionsManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterService, setFilterService] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page, setPage] = useState(0);

  // Plans for the assign modal
  const [cmmsPlans, setCmmsPlans] = useState([]);
  const [chatbotPlans, setChatbotPlans] = useState([]);

  // Assign modal state
  const [assignTarget, setAssignTarget] = useState(null); // { company, serviceType, existingSub }
  const [assignForm, setAssignForm] = useState({ serviceType: 'CHATBOT', planId: '', billingCycle: 'MONTHLY', isTrial: false });
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const paged = await adminCompanyService.getPaged(null, null, 0, 1000);
      const companies = paged?.content || [];

      const allRows = [];
      await Promise.all(
        companies.map(async (company) => {
          const [cmms, chatbot] = await Promise.all([
            adminSubscriptionService.getCmmsForCompany(company.id),
            adminSubscriptionService.getChatbotForCompany(company.id),
          ]);
          if (cmms) allRows.push({ company, subscription: cmms, serviceType: 'CMMS' });
          if (chatbot) allRows.push({ company, subscription: chatbot, serviceType: 'CHATBOT' });
          if (!cmms && !chatbot) allRows.push({ company, subscription: null, serviceType: null });
        })
      );

      allRows.sort((a, b) => {
        const aHas = a.subscription ? 1 : 0;
        const bHas = b.subscription ? 1 : 0;
        if (aHas !== bHas) return bHas - aHas;
        return a.company.name.localeCompare(b.company.name);
      });

      setRows(allRows);
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRows(); }, [loadRows]);

  // Load all plans (including inactive) for the assign modal
  useEffect(() => {
    Promise.all([
      adminPlansService.getAllCmmsPlans(),
      adminPlansService.getAllChatbotPlans(),
    ]).then(([cmms, chatbot]) => {
      setCmmsPlans(Array.isArray(cmms) ? cmms : []);
      setChatbotPlans(Array.isArray(chatbot) ? chatbot : []);
    }).catch(() => {});
  }, []);

  // ── Assign modal helpers ─────────────────────────────────────────────────

  const openAssignModal = (company, serviceType, existingSub = null) => {
    setAssignTarget({ company, serviceType, existingSub });
    setAssignForm({
      serviceType: serviceType || 'CHATBOT',
      planId: existingSub?.planId ? String(existingSub.planId) : '',
      billingCycle: existingSub?.billingCycle || 'MONTHLY',
      isTrial: false,
    });
    setAssignError('');
  };

  const handleAssignSubmit = async () => {
    const { company, serviceType, existingSub } = assignTarget;
    if (!assignForm.isTrial && !assignForm.planId) {
      setAssignError('Please select a plan or enable trial.');
      return;
    }
    setAssignLoading(true);
    setAssignError('');
    try {
      if (existingSub) {
        // Change plan on existing subscription
        if (serviceType === 'CMMS') {
          await adminSubscriptionService.upgradeCmms(company.id, assignForm.planId);
        } else {
          await adminSubscriptionService.upgradeChatbot(company.id, assignForm.planId);
        }
      } else {
        // Create new subscription
        if (serviceType === 'CMMS') {
          await adminSubscriptionService.assignCmms({
            companyId: company.id,
            cmmsTenantId: String(company.id),
            planId: assignForm.isTrial ? null : Number(assignForm.planId),
            billingCycle: assignForm.isTrial ? null : assignForm.billingCycle,
            isTrialPeriod: assignForm.isTrial,
            companyName: company.name,
          });
        } else {
          await adminSubscriptionService.assignChatbot({
            companyId: company.id,
            chatbotTenantId: String(company.id),
            planId: assignForm.isTrial ? null : Number(assignForm.planId),
            billingCycle: assignForm.isTrial ? null : assignForm.billingCycle,
            isTrialPeriod: assignForm.isTrial,
            companyName: company.name,
          });
        }
      }
      setAssignTarget(null);
      await loadRows();
    } catch (err) {
      setAssignError(err?.message || 'Failed to save subscription. Please try again.');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleCancelSub = async (company, serviceType) => {
    if (!window.confirm(`Cancel ${serviceType} subscription for "${company.name}"?`)) return;
    try {
      if (serviceType === 'CMMS') await adminSubscriptionService.cancelCmms(company.id);
      else await adminSubscriptionService.cancelChatbot(company.id);
      setRows((prev) =>
        prev.map((r) =>
          r.company.id === company.id && r.serviceType === serviceType
            ? { ...r, subscription: { ...r.subscription, status: 'CANCELLED' } }
            : r
        )
      );
    } catch {
      alert('Failed to cancel subscription.');
    }
  };

  // ── Badges ───────────────────────────────────────────────────────────────

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ACTIVE':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />Active
          </span>
        );
      case 'CANCELLED':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />Cancelled
          </span>
        );
      case 'PAST_DUE':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <AlertTriangle className="w-3 h-3" />Past Due
          </span>
        );
      case 'TRIAL':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />Trial
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />{status || '—'}
          </span>
        );
    }
  };

  const getServiceBadge = (serviceType) => {
    if (serviceType === 'CMMS')
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
          <Wrench className="w-3 h-3" />CMMS
        </span>
      );
    if (serviceType === 'CHATBOT')
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
          <Bot className="w-3 h-3" />Chatbot
        </span>
      );
    return <span className="text-sm text-gray-400">—</span>;
  };

  // ── Filtering / pagination ───────────────────────────────────────────────

  const filteredRows = rows.filter((item) => {
    const matchesSearch =
      searchTerm === '' ||
      item.company.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesService =
      filterService === 'all' ||
      item.serviceType?.toLowerCase() === filterService;
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'none'
        ? !item.subscription
        : item.subscription?.status === filterStatus);
    return matchesSearch && matchesService && matchesStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const paginatedRows = filteredRows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const activeCount = rows.filter((r) => r.subscription?.status === 'ACTIVE').length;
  const trialCount  = rows.filter((r) => r.subscription?.status === 'TRIAL').length;
  const noSubCount  = rows.filter((r) => !r.subscription).length;

  const activePlans = assignForm.serviceType === 'CMMS' ? cmmsPlans : chatbotPlans;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Subscriptions Management</h1>
          <p className="text-gray-600 mt-1">Monitor and manage all platform subscriptions</p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </button>
          <Link
            to="/admin/plans"
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Manage Plans
          </Link>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex gap-1 border-b border-gray-200 px-4">
          {['overview', 'blocks-slots'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'overview' ? 'Subscriptions Overview' : 'Blocks & Slots'}
            </button>
          ))}
        </div>
      </div>

      {/* Blocks & Slots Tab */}
      {activeTab === 'blocks-slots' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <BlockSlotConfig />
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="text-sm text-gray-500">Active Subscriptions</div>
              <div className="text-3xl font-bold text-green-600 mt-1">{activeCount}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="text-sm text-gray-500">Trial Subscriptions</div>
              <div className="text-3xl font-bold text-blue-600 mt-1">{trialCount}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="text-sm text-gray-500">No Subscription</div>
              <div className="text-3xl font-bold text-gray-400 mt-1">{noSubCount}</div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by company name..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-3">
                <select
                  value={filterService}
                  onChange={(e) => { setFilterService(e.target.value); setPage(0); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Services</option>
                  <option value="cmms">CMMS</option>
                  <option value="chatbot">Chatbot</option>
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Statuses</option>
                  <option value="ACTIVE">Active</option>
                  <option value="TRIAL">Trial</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="none">No Subscription</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Next Billing</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                        No subscriptions found
                      </td>
                    </tr>
                  ) : (
                    paginatedRows.map((item, index) => {
                      const sub = item.subscription;
                      const company = item.company;
                      const isCancelled = sub?.status === 'CANCELLED';
                      return (
                        <tr key={`${company.id}-${item.serviceType || 'none'}-${index}`} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">{company.name}</div>
                            <div className="text-sm text-gray-500">{company.domain || company.email || '—'}</div>
                          </td>
                          <td className="px-6 py-4">
                            {getServiceBadge(item.serviceType)}
                          </td>
                          <td className="px-6 py-4">
                            {sub ? (
                              <>
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                  {sub.planName || 'Unknown Plan'}
                                </span>
                                <div className="text-xs text-gray-500 mt-1 capitalize">
                                  {sub.billingCycle?.toLowerCase() || '—'}
                                </div>
                              </>
                            ) : (
                              <span className="text-sm text-gray-400">No subscription</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {sub ? (
                              <div className="flex items-center gap-1 font-medium text-gray-900">
                                <DollarSign className="w-4 h-4 text-gray-400" />
                                {sub.price || 0}/{sub.billingCycle === 'ANNUAL' ? 'yr' : 'mo'}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {sub ? getStatusBadge(sub.status) : <span className="text-sm text-gray-400">—</span>}
                          </td>
                          <td className="px-6 py-4">
                            {sub?.nextBillingDate ? (
                              <div className="flex items-center gap-1 text-gray-700">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                {new Date(sub.nextBillingDate).toLocaleDateString()}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {!sub || isCancelled ? (
                                /* Assign buttons for companies with no / cancelled subscription */
                                <>
                                  <button
                                    onClick={() => openAssignModal(company, 'CHATBOT')}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                                  >
                                    <Plus className="w-3 h-3" />Chatbot
                                  </button>
                                  <button
                                    onClick={() => openAssignModal(company, 'CMMS')}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                                  >
                                    <Plus className="w-3 h-3" />CMMS
                                  </button>
                                </>
                              ) : (
                                /* Change plan / Cancel for active subscriptions */
                                <>
                                  <button
                                    onClick={() => openAssignModal(company, item.serviceType, sub)}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                                  >
                                    <Settings className="w-3 h-3" />Change Plan
                                  </button>
                                  <button
                                    onClick={() => handleCancelSub(company, item.serviceType)}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                                  >
                                    <XCircle className="w-3 h-3" />Cancel
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{paginatedRows.length}</span> of{' '}
                <span className="font-medium">{filteredRows.length}</span> entries
                {(searchTerm || filterService !== 'all' || filterStatus !== 'all') &&
                  ` (filtered from ${rows.length} total)`}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">
                  Page {safePage + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={safePage >= totalPages - 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Assign / Change Plan Modal ──────────────────────────────────────── */}
      {assignTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {assignTarget.existingSub ? 'Change Plan' : 'Assign Subscription'}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">{assignTarget.company.name}</p>
              </div>
              <button
                onClick={() => setAssignTarget(null)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-5 space-y-4">
              {/* Service type — read-only when changing plan on existing sub */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
                {assignTarget.existingSub ? (
                  <div className="flex items-center gap-2">
                    {getServiceBadge(assignTarget.serviceType)}
                    <span className="text-sm text-gray-500">(locked — changing plan only)</span>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    {['CHATBOT', 'CMMS'].map((st) => (
                      <button
                        key={st}
                        onClick={() => setAssignForm((f) => ({ ...f, serviceType: st, planId: '' }))}
                        className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          assignForm.serviceType === st
                            ? st === 'CHATBOT'
                              ? 'bg-purple-600 text-white border-purple-600'
                              : 'bg-orange-500 text-white border-orange-500'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {st === 'CHATBOT' ? '🤖 Chatbot' : '🔧 CMMS'}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Trial toggle — only for new subscriptions */}
              {!assignTarget.existingSub && (
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={assignForm.isTrial}
                    onChange={(e) => setAssignForm((f) => ({ ...f, isTrial: e.target.checked, planId: '' }))}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Start as trial (no plan required)</span>
                </label>
              )}

              {/* Plan selector */}
              {!assignForm.isTrial && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                  <select
                    value={assignForm.planId}
                    onChange={(e) => setAssignForm((f) => ({ ...f, planId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">— Select a plan —</option>
                    {activePlans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.planName} — ${p.monthlyPrice}/mo
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Billing cycle */}
              {!assignForm.isTrial && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle</label>
                  <div className="flex gap-3">
                    {['MONTHLY', 'ANNUAL'].map((cycle) => (
                      <button
                        key={cycle}
                        onClick={() => setAssignForm((f) => ({ ...f, billingCycle: cycle }))}
                        className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          assignForm.billingCycle === cycle
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {cycle === 'MONTHLY' ? 'Monthly' : 'Annual (save ~20%)'}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {assignError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {assignError}
                </p>
              )}
            </div>

            {/* Modal footer */}
            <div className="flex gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setAssignTarget(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignSubmit}
                disabled={assignLoading}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {assignLoading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4" />
                )}
                {assignTarget.existingSub ? 'Change Plan' : 'Assign Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionsManagement;
